/**
 * FEAT-010 — Vehicle profile (workspace-wide, NOT per-station).
 *
 * Power caps + battery size + default start SoC, plus an OPTIONAL DC charging
 * curve (max accepted power as a function of SoC — real EVs taper toward the
 * top of the pack). When a curve is present it shapes the DC power per tick;
 * when absent the flat `maxDcPowerKw` cap is used. AC charging stays flat.
 * DC power for an active transaction is `min(vehicleDcPower(soc), connector /
 * evse / cp budget)`.
 *
 * Invariants are enforced via zod at the wire boundary (import JSON,
 * inbound from CSMS) and via constructor-style helpers in the domain
 * (pure functions; no React, no Dexie).
 */
import { z } from 'zod';
import type { VehicleProfileId } from './types';

/** A single breakpoint on a DC charging curve: max accepted power at a SoC. */
export interface ChargingCurvePoint {
  /** State of charge, [0, 100]. */
  readonly socPct: number;
  /** Max DC power the vehicle accepts at this SoC, kW. ≥ 0. */
  readonly powerKw: number;
}

export interface VehicleProfileSnapshot {
  readonly id: VehicleProfileId;
  readonly name: string;
  /** Battery capacity in kWh. > 0. */
  readonly batteryKwh: number;
  /** Max AC power the vehicle accepts, kW. ≥ 0 (0 means "DC only"). */
  readonly maxAcPowerKw: number;
  /** Max DC power the vehicle accepts, kW. ≥ 0 (0 means "AC only"). */
  readonly maxDcPowerKw: number;
  /** Default starting SoC when the vehicle is first bound to a DC connector. [0, 100]. */
  readonly defaultStartSocPct: number;
  /**
   * Optional DC charging curve. When present (≥ 1 point) it overrides the flat
   * `maxDcPowerKw` cap: the accepted DC power is interpolated from this curve
   * at the connector's current SoC. Absent / empty → flat `maxDcPowerKw`.
   */
  readonly chargingCurve?: readonly ChargingCurvePoint[];
  /** True for the 5 seeded presets (FEAT-015), false for user-created profiles. */
  readonly isPreset: boolean;
  /** Local audit timestamp (ms since epoch). */
  readonly createdAt: number;
}

/**
 * Zod schema for the wire / import shape. The branded VehicleProfileId
 * is validated as a UUID v4 string; if missing/empty the import flow
 * mints a fresh id rather than rejecting the row.
 */
export const chargingCurvePointSchema = z.object({
  socPct: z.number().min(0).max(100),
  powerKw: z.number().min(0),
});

export const vehicleProfileSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(64),
  batteryKwh: z.number().positive(),
  maxAcPowerKw: z.number().min(0),
  maxDcPowerKw: z.number().min(0),
  defaultStartSocPct: z.number().min(0).max(100),
  chargingCurve: z.array(chargingCurvePointSchema).optional(),
  isPreset: z.boolean().optional(),
  createdAt: z.number().int().nonnegative().optional(),
});

export type VehicleProfileInput = z.infer<typeof vehicleProfileSchema>;

/**
 * Compute the effective charging power for a connector/vehicle pair.
 *
 *   effectiveWatts = min(vehicleMaxW, connectorMaxW, evseMaxW, cpMaxW)
 *
 * `vehicleMaxW` depends on the connector type (AC vs DC). Returns 0 when
 * the vehicle has no power on that side (e.g. AC-only vehicle on a DC
 * connector → 0, so no current flows).
 */
export function computeEffectiveWatts(opts: {
  readonly vehicle: VehicleProfileSnapshot;
  readonly connectorType: 'AC' | 'DC';
  readonly connectorMaxW: number;
  readonly evseMaxW: number;
  readonly cpMaxW: number;
  /**
   * Current SoC, used only on DC when the vehicle has a charging curve. When
   * omitted (or no curve), the flat `maxDcPowerKw` cap is used.
   */
  readonly socPct?: number;
}): number {
  let vehicleMaxW: number;
  if (opts.connectorType === 'DC') {
    const curve = opts.vehicle.chargingCurve;
    vehicleMaxW =
      curve && curve.length > 0 && opts.socPct !== undefined
        ? chargePowerKwAtSoc(curve, opts.socPct) * 1000
        : opts.vehicle.maxDcPowerKw * 1000;
  } else {
    vehicleMaxW = opts.vehicle.maxAcPowerKw * 1000;
  }
  if (vehicleMaxW <= 0) return 0;
  return Math.max(
    0,
    Math.min(vehicleMaxW, opts.connectorMaxW, opts.evseMaxW, opts.cpMaxW),
  );
}

/**
 * DC charge power (kW) the vehicle accepts at a given SoC, linearly
 * interpolated between curve breakpoints. Below the first / above the last
 * point the curve is held flat. Points need not be pre-sorted. Returns 0 for
 * an empty curve.
 */
export function chargePowerKwAtSoc(
  curve: readonly ChargingCurvePoint[],
  socPct: number,
): number {
  const pts = [...curve].sort((a, b) => a.socPct - b.socPct);
  const first = pts[0];
  if (first === undefined) return 0;
  if (socPct <= first.socPct) return first.powerKw;
  const last = pts[pts.length - 1];
  if (last === undefined) return 0;
  if (socPct >= last.socPct) return last.powerKw;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (a === undefined || b === undefined) continue;
    if (socPct >= a.socPct && socPct <= b.socPct) {
      const span = b.socPct - a.socPct;
      if (span <= 0) return a.powerKw;
      const f = (socPct - a.socPct) / span;
      return a.powerKw + f * (b.powerKw - a.powerKw);
    }
  }
  return last.powerKw;
}

/**
 * Compute the SoC delta over `deltaSec` seconds at `effectiveWatts`
 * draw against a `batteryKwh` battery. Returned in percentage points.
 *
 *   deltaSoc% = (W * deltaSec / 3600) / batteryKwh / 10
 *             = (kWh delivered) / batteryKwh * 100
 *
 * Caller clamps the running SoC to [0, 100].
 */
export function computeDeltaSocPct(opts: {
  readonly effectiveWatts: number;
  readonly batteryKwh: number;
  readonly deltaSec: number;
}): number {
  if (opts.batteryKwh <= 0) return 0;
  if (opts.effectiveWatts <= 0 || opts.deltaSec <= 0) return 0;
  const kwhDelivered = (opts.effectiveWatts * opts.deltaSec) / 1000 / 3600;
  return (kwhDelivered / opts.batteryKwh) * 100;
}

/** Apply a delta and clamp to [0, 100]. */
export function clampSocPct(current: number, delta: number): number {
  const next = current + delta;
  if (next < 0) return 0;
  if (next > 100) return 100;
  return next;
}
